/**
 * Email Template Service
 * Service for managing Mailtrap email templates metadata
 */

import { supabase } from '@xanectech/supabase-client';
import { createLogger } from '@xanectech/lib';
import type { Database } from '@xanectech/types';

type EmailTemplateRow = Database['public']['Tables']['email_templates']['Row'];
type EmailTemplateInsert =
  Database['public']['Tables']['email_templates']['Insert'];
type EmailTemplateUpdate =
  Database['public']['Tables']['email_templates']['Update'];

const logger = createLogger('Data - EmailTemplateService');

export interface EmailTemplate extends EmailTemplateRow {
  // Additional computed properties if needed
}

export interface CreateEmailTemplateData {
  template_id: string;
  name: string;
  subject: string;
  template_variables?: Record<string, unknown>;
  is_active?: boolean;
}

export interface UpdateEmailTemplateData {
  template_id?: string;
  name?: string;
  subject?: string;
  template_variables?: Record<string, unknown>;
  is_active?: boolean;
}

interface SyncMailtrapTemplatesResponse {
  count: number;
}

interface SyncMailtrapTemplatesInvokeResult {
  data: SyncMailtrapTemplatesResponse | null;
  error: Error | null;
}

export class EmailTemplateService {
  /**
   * Get all email templates
   */
  static async getAllEmailTemplates(): Promise<EmailTemplate[]> {
    const { data, error } = await supabase
      .from('email_templates')
      .select('*')
      .order('name');

    if (error) {
      logger.error('Error fetching email templates:', { error });
      throw error;
    }

    logger.info(`Fetched ${data?.length || 0} email templates`);
    return data || [];
  }

  /**
   * Get active email templates only
   */
  static async getActiveEmailTemplates(): Promise<EmailTemplate[]> {
    const { data, error } = await supabase
      .from('email_templates')
      .select('*')
      .eq('is_active', true)
      .order('name');

    if (error) {
      logger.error('Error fetching active email templates:', { error });
      throw error;
    }

    logger.info(`Fetched ${data?.length || 0} active email templates`);
    return data || [];
  }

  /**
   * Get a single email template by UUID
   */
  static async getEmailTemplate(uuid: string): Promise<EmailTemplate | null> {
    const { data, error } = await supabase
      .from('email_templates')
      .select('*')
      .eq('uuid', uuid)
      .single();

    if (error) {
      logger.error('Error fetching email template:', { error, uuid });
      throw error;
    }

    logger.info('Fetched email template', { uuid });
    return data;
  }

  /**
   * Get email template by Mailtrap template_id
   */
  static async getEmailTemplateByMailtrapId(
    template_id: string,
  ): Promise<EmailTemplate | null> {
    const { data, error } = await supabase
      .from('email_templates')
      .select('*')
      .eq('template_id', template_id)
      .single();

    if (error) {
      if (error.code === 'PGRST116') {
        // Not found
        return null;
      }
      logger.error('Error fetching email template by template_id:', {
        error,
        template_id,
      });
      throw error;
    }

    logger.info('Fetched email template by template_id', { template_id });
    return data;
  }

  /**
   * Create a new email template
   */
  static async createEmailTemplate(
    templateData: CreateEmailTemplateData,
  ): Promise<EmailTemplate> {
    const insertData: EmailTemplateInsert = {
      template_id: templateData.template_id,
      name: templateData.name,
      subject: templateData.subject,
      template_variables:
        JSON.stringify(templateData.template_variables, null, 2) || {},
      is_active: templateData.is_active ?? true,
    };

    const { data, error } = await supabase
      .from('email_templates')
      .insert(insertData)
      .select()
      .single();

    if (error) {
      logger.error('Error creating email template:', {
        error,
        templateData: templateData as unknown as Record<string, unknown>,
      });
      throw error;
    }

    logger.info('Created email template', { uuid: data.uuid });
    return data;
  }

  /**
   * Update an email template
   */
  static async updateEmailTemplate(
    uuid: string,
    updates: UpdateEmailTemplateData,
  ): Promise<EmailTemplate> {
    const updateData: EmailTemplateUpdate = {
      ...(updates.template_id && { template_id: updates.template_id }),
      ...(updates.name && { name: updates.name }),
      ...(updates.subject && { subject: updates.subject }),
      ...(updates.template_variables !== undefined && {
        template_variables: JSON.stringify(updates.template_variables, null, 2),
      }),
      ...(updates.is_active !== undefined && { is_active: updates.is_active }),
    };

    const { data, error } = await supabase
      .from('email_templates')
      .update(updateData)
      .eq('uuid', uuid)
      .select()
      .single();

    if (error) {
      logger.error('Error updating email template:', {
        error,
        uuid,
        updates: updates as unknown as Record<string, unknown>,
      });
      throw error;
    }

    logger.info('Updated email template', { uuid });
    return data;
  }

  /**
   * Toggle email template active status
   */
  static async toggleEmailTemplateActive(
    uuid: string,
    is_active: boolean,
  ): Promise<EmailTemplate> {
    const { data, error } = await supabase
      .from('email_templates')
      .update({ is_active })
      .eq('uuid', uuid)
      .select()
      .single();

    if (error) {
      logger.error('Error toggling email template active status:', {
        error,
        uuid,
        is_active,
      });
      throw error;
    }

    logger.info('Toggled email template active status', { uuid, is_active });
    return data;
  }

  /**
   * Delete an email template (hard delete)
   */
  static async deleteEmailTemplate(uuid: string): Promise<void> {
    const { error } = await supabase
      .from('email_templates')
      .delete()
      .eq('uuid', uuid);

    if (error) {
      logger.error('Error deleting email template:', { error, uuid });
      throw error;
    }

    logger.info('Deleted email template', { uuid });
  }

  /**
   * Search email templates by name
   */
  static async searchEmailTemplates(
    query: string,
    limit: number = 20,
  ): Promise<EmailTemplate[]> {
    const { data, error } = await supabase
      .from('email_templates')
      .select('*')
      .ilike('name', `%${query}%`)
      .order('name')
      .limit(limit);

    if (error) {
      logger.error('Error searching email templates:', { error, query });
      throw error;
    }

    logger.info(`Found ${data?.length || 0} email templates matching query`, {
      query,
    });
    return data || [];
  }

  /**
   * Get email template statistics
   */
  static async getEmailTemplateStats(): Promise<{
    total: number;
    active: number;
    inactive: number;
  }> {
    // Get total count
    const { count: total, error: totalError } = await supabase
      .from('email_templates')
      .select('*', { count: 'exact', head: true });

    if (totalError) {
      logger.error('Error fetching total email templates count:', {
        error: totalError,
      });
      throw totalError;
    }

    // Get active count
    const { count: active, error: activeError } = await supabase
      .from('email_templates')
      .select('*', { count: 'exact', head: true })
      .eq('is_active', true);

    if (activeError) {
      logger.error('Error fetching active email templates count:', {
        error: activeError,
      });
      throw activeError;
    }

    const stats = {
      total: total || 0,
      active: active || 0,
      inactive: (total || 0) - (active || 0),
    };

    logger.info('Fetched email template stats', { stats });
    return stats;
  }

  /**
   * Sync all templates from Mailtrap into the local email_templates table.
   * Upserts records keyed on template_id; new templates are inserted, existing
   * ones are updated with fresh name/subject values from Mailtrap.
   *
   * @returns The number of templates upserted
   */
  static async syncMailtrapTemplates(): Promise<number> {
    const { data, error } =
      (await supabase.functions.invoke<SyncMailtrapTemplatesResponse>(
        'sync-mailtrap-templates',
        {
          body: {},
        },
      )) as SyncMailtrapTemplatesInvokeResult;

    if (error) {
      logger.error('Error invoking sync-mailtrap-templates function:', {
        error,
      });
      throw error;
    }

    const count = typeof data?.count === 'number' ? data.count : 0;
    logger.info(`Synced ${count} templates from Mailtrap`);
    return count;
  }
}
